import { connect, type DispatchProp } from "react-redux";

import {
	ExclamationCircleFilled,
	LoadingOutlined,
	PlusOutlined,
} from "@ant-design/icons";
import type { Dispatch } from "@reduxjs/toolkit";
import type { FormInstance } from "antd";
import { Button, Form, Spin } from "antd";
import type { AnyObject } from "antd/es/_util/type";
import { actions as routerActions } from "redux-router5";
import type { State as RouterState } from "router5";
import ConnectedAppLayout from "../AppLayout.tsx";
import type { RootState } from "../store.ts";
import * as commonColumns from "../../common/columns.tsx";
import { Card } from "../../common/components/Card.tsx";
import { ListActionBar } from "../../common/components/ListActionBar.tsx";
import { PageTitleContainer } from "../../common/components/PageTitle.tsx";
import {
	TableWrapper,
	type PaginationAndFilter,
	type TableWrapperProps,
} from "../../common/components/Table.tsx";
import { servicesFilter } from "../common/filter.ts";
import { sanitizedFilter } from "../../common/functions.ts";
import { routeNames } from "../common/routes.ts";
import { servicesActions } from "../../features/services/servicesSlice.ts";
import type {
	PaginatedServicesList,
	Services,
} from "../generated-axios-client/index.ts";

const onFilter =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		form
			.validateFields()
			.then((values) => {
				dispatch(
					routerActions.navigateTo(
						currentRoute.name,
						sanitizedFilter({ ...currentRoute.params, ...values, page: 1 }),
					),
				);
			})
			.catch((error) => {
				console.log("Form validation error:", error);
			});
	};

const onCancel =
	(dispatch: Dispatch, form: FormInstance, currentRoute: RouterState) => () => {
		dispatch(
			routerActions.navigateTo(
				currentRoute.name,
				sanitizedFilter({ ...currentRoute.params, ...servicesFilter, page: 1 }),
			),
		);
		form.resetFields();
	};

const getColumns: TableWrapperProps<
	PaginatedServicesList,
	AnyObject
>["getColumns"] = (intl) => [
	commonColumns.simpleColumn("row_number", "table_column_ticket_number", intl),
	commonColumns.simpleColumn("name", "table_column_name", intl),
	commonColumns.simpleColumn("description", "service_description", intl),
	commonColumns.simpleColumn("created_at", "table_column_created_at", intl),
	commonColumns.simpleColumn("actions", "table_column_actions", intl),
];

const getTableRows: TableWrapperProps<
	PaginatedServicesList,
	AnyObject
>["getRows"] = (intl, dispatch, data, modalApi) => {
	return data.results.map((service: Services, index: number) => {
		const showDeleteConfirm = () => {
			modalApi?.confirm({
				title: intl.formatMessage({ id: "service_delete_confirmation" }),
				icon: <ExclamationCircleFilled />,
				okText: intl.formatMessage({ id: "confirm" }),
				okType: "danger",
				cancelText: intl.formatMessage({ id: "cancel" }),
				onOk() {
					if (service.id) {
						dispatch(servicesActions.deleteService(service.id));
					}
				},
				onCancel() {},
			});
		};

		const onView = () => {
			dispatch(
				routerActions.navigateTo(routeNames.SETTINGS_SERVICE_VIEW, {
					id: service.id,
				}),
			);
		};

		const onEdit = () => {
			dispatch(
				routerActions.navigateTo(routeNames.SETTINGS_SERVICE_EDIT, {
					id: service.id,
				}),
			);
		};

		return {
			key: service.id,
			row_number: index + 1,
			name: service.name,
			description: service.description,
			created_at: service.created_at
				? commonColumns.dateValue(service.created_at, intl)
				: null,
			actions: commonColumns.actionsValue(
				undefined,
				undefined,
				undefined,
				undefined,
				onView,
				onEdit,
				showDeleteConfirm,
				undefined,
				"Service",
			),
		};
	});
};

type ServicesPageProps = ReturnType<typeof withServices> & DispatchProp;

export const ServicesPage = (props: ServicesPageProps) => {
	const { results, dispatch, currentRoute, intl, modalApi } = props;

	const [form] = Form.useForm();

	if (!currentRoute) {
		return <Spin indicator={<LoadingOutlined spin />} size="large" />;
	}

	const pagination = results.pagination;

	return (
		<ConnectedAppLayout>
			<PageTitleContainer
				title={intl.formatMessage({ id: "service_table_header_name" })}
				subtitle={intl.formatMessage(
					{ id: "service_table_header_number_of_items" },
					{ no: pagination.total_items },
				)}
				extra={
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() =>
							dispatch(
								routerActions.navigateTo(routeNames.SETTINGS_SERVICE_CREATE),
							)
						}
					>
						{intl.formatMessage({ id: "service_create_button" })}
					</Button>
				}
			>
				<Card
					title={
						<ListActionBar
							intl={intl}
							form={form}
							onFilter={onFilter(dispatch, form, currentRoute)}
							onCancel={onCancel(dispatch, form, currentRoute)}
							filterTemplate={servicesFilter}
							currentFilter={currentRoute.params}
							searchBarPlaceholderKey={intl.formatMessage({
								id: "service_table_header_search_placeholder",
							})}
						/>
					}
				>
					<TableWrapper
						data={results}
						getRows={getTableRows}
						getColumns={getColumns}
						intl={intl}
						modalApi={modalApi}
						route={currentRoute}
						applyFilterAction={(filterParams) =>
							routerActions.navigateTo(
								routeNames.SETTINGS_SERVICE_LIST,
								filterParams,
							)
						}
						pagination={pagination as PaginationAndFilter}
					/>
				</Card>
			</PageTitleContainer>
		</ConnectedAppLayout>
	);
};

const withServices = (state: RootState) => ({
	results: state.services.list,
	currentRoute: state.router.route,
	intl: state.intl.datetimeIntl,
	modalApi: state.appFeedback.modal,
});

export const ConnectedServicesPage = connect(withServices)(ServicesPage);
export default ConnectedServicesPage;
